import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { unsafeSVG } from "lit/directives/unsafe-svg.js";

const iconCache = new Map<string, Promise<string>>();

function loadIcon(url: string) {
  if (!iconCache.has(url)) {
    iconCache.set(
      url,
      fetch(url, { credentials: "same-origin" }).then(async (r) => {
        if (!r.ok) throw new Error(`Failed to load ${url} (${r.status})`);
        return r.text();
      }),
    );
  }
  return iconCache.get(url)!;
}

@customElement("ehm-icon")
export class EhmIcon extends LitElement {
  static styles = css`
    :host {
      width: var(--icon-size, 1.25rem);
      min-width: var(--icon-size, 1.25rem);
      height: var(--icon-size, 1.25rem);
      line-height: 1;
      color: currentColor;
      position: relative;
      top: var(--icon-offset, 0);
      display: block;
      transform: rotate(var(--icon-rotate, 0deg));
    }
    svg {
      width: 100%;
      height: 100%;
      display: block;
      fill: var(--icon-color, currentColor);
    }
    svg.stroke {
      stroke: var(--icon-color, currentColor);
      stroke-width: var(--icon-stroke-width, 1.5px);
      fill: none;
    }
  `;

  /** Icon name without extension, e.g. "search" */
  @property({ type: String }) name = "";
  @property({ type: Boolean }) isStroke = false;
  @property({ type: String }) arialabel = "";

  /** Base folder where SVGs live (absolute or relative to site root) */
  @property({ type: String }) base = "/assets/icons";

  /** Optional explicit URL to an SVG (overrides base+name). */
  @property({ type: String }) src?: string;

  @state() private _svg: string | null = null;
  @state() private _error: string | null = null;
  #token = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#load();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has("name") || changed.has("base") || changed.has("src")) {
      this.#load();
    }
  }

  render() {
    // Nothing while loading; you can style a skeleton via ::part(fallback)
    if (this._svg) return html`${unsafeSVG(this._svg)}`;
    if (this._error) return html`<span part="fallback" aria-hidden="true"></span>`;
    return nothing;
  }

  async #load() {
    this._svg = null;
    this._error = null;
    const url = this.src ?? this.#urlFromProps();
    if (!url) return;

    const t = ++this.#token;
    try {
      const raw = await loadIcon(url);
      if (t !== this.#token) return; // ignore stale loads
      this._svg = this.#normalize(raw);
    } catch {
      if (t !== this.#token) return;
      this._error = "Icon load error";
    }
  }

  #urlFromProps() {
    if (!this.name) return "";
    const base = this.base.replace(/\/+$/, "");
    return `${base}/${this.name}.svg`;
  }

  // Make the SVG styleable with currentColor and size from CSS.
  #normalize(svgText: string) {
    try {
      const doc = new DOMParser().parseFromString(svgText, "image/svg+xml");
      const svg = doc.documentElement;

      svg.setAttribute("focusable", "false");
      if (this.arialabel) {
        svg.setAttribute("aria-label", this.arialabel);
      } else {
        svg.setAttribute("aria-hidden", "true");
      }
      if (this.isStroke) svg.classList.add("stroke");

      return new XMLSerializer().serializeToString(svg);
    } catch {
      return svgText; // fall back to raw if parsing fails
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ehm-icon": EhmIcon;
  }
}
